import React from 'react';
import { useInView } from './useInView'; // Assuming useInView is in a file named useInView.js

/**
 * A component that lazy-loads its children.
 * It will only render its children when it comes into view for the first time.
 * After the initial render, the children remain mounted even if the component scrolls out of view.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The content to be lazy-loaded.
 * @param {object} [props.options] - Options for the IntersectionObserver (threshold, root, rootMargin).
 * @param {React.CSSProperties} [props.placeholderStyle] - Inline styles for the placeholder div.
 * @param {React.CSSProperties} [props.contentStyle] - Inline styles for the content div once rendered.
 */
function LazyLoadComponent({ children, options, placeholderStyle, contentStyle }) {
  // Destructure options for useInView, providing defaults if not passed
  const { threshold = 0.1, root = null, rootMargin = '0px' } = options || {};

  const [setRef, isInView, hasRenderedOnce] = useInView(threshold, root, rootMargin);

  // Default placeholder style for visual feedback
  const defaultPlaceholderStyle = {
    minHeight: '800px',

  };

  // Default content style once rendered
  const defaultContentStyle = {
    // minHeight: '200px',
  };

  // Only render the children if the component is in view or has rendered once
  if (!isInView && !hasRenderedOnce) {
    return (
      <div
        ref={setRef}
        style={{ ...defaultPlaceholderStyle, ...placeholderStyle , }}
        aria-label="Scrolling to reveal content"
        role="region"
      >
        <div className='d-none' ></div>
      </div>
    );
  }

  // Once isInView becomes true (or hasRenderedOnce is true), it will render the children
  return (
    <div
      ref={setRef}
      style={{ ...defaultContentStyle, ...contentStyle }}
    >
      {children}
    </div>
  );
}

export default LazyLoadComponent;